import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../services/supabase.ts';
import { Tenant } from '../types/tenant.ts';
import { formatCurrency, getOrdinalSuffix, formatDate, formatPhoneNumber } from '../utils/formatters.ts';
import DocumentUpload from '../components/tenants/DocumentUpload.tsx';
import LoadingSpinner from '../components/shared/LoadingSpinner.tsx';
import ErrorMessage from '../components/shared/ErrorMessage.tsx';
import '../styles/TenantDetails.css';
import TenantBills from '../components/tenants/TenantBills.tsx';
import TenantPayments from '../components/tenants/TenantPayments.tsx';

const TenantDetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'bills' | 'payments'>('bills');

  const fetchTenantDetails = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select(`
          *,
          property:properties(*)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      setTenant(data);
    } catch (err) {
      setError('Failed to load tenant details');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTenantDetails();
  }, [fetchTenantDetails]);

  const getStatusClass = (status: string) => {
    return `status-badge ${status.toLowerCase()}`;
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!tenant) return <ErrorMessage message="Tenant not found" />;

  return (
    <div className="tenant-details-page">
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1>{tenant.full_name}</h1>
      </div>

      <div className="details-grid">
        <div className="detail-card">
          <h3>Personal Information</h3>
          <div className="detail-content">
            <p><strong>Phone:</strong> {formatPhoneNumber(tenant.phone)}</p>
            <p><strong>Email:</strong> {tenant.email ? 
              tenant.email : 
              <span className="not-provided">Email not provided</span>
            }</p>
            <p><strong>Emergency Contact:</strong> {tenant.emergency_contact ? 
              tenant.emergency_contact : 
              <span className="not-provided">Emergency contact not provided</span>
            }</p>
            <p><strong>Aadhar No:</strong> {tenant.aadhar_no ? 
              tenant.aadhar_no : 
              <span className="not-provided">Aadhar number not provided</span>
            }</p>
            <p><strong>PAN No:</strong> {tenant.pan_no ? 
              tenant.pan_no : 
              <span className="not-provided">PAN number not provided</span>
            }</p>
            <div className="detail-item">
              <label>Status</label>
              <span className={getStatusClass(tenant.status)}>
                <span className="status-dot"></span>
                {tenant.status}
              </span>
            </div>
          </div>
        </div>

        {tenant.status === 'Active' ? (
          <div className="detail-card">
            <h3>Current Lease Information</h3>
            <div className="detail-content">
              <p><strong>Property:</strong> {tenant.property?.name}</p>
              <p><strong>Monthly Rent:</strong> {formatCurrency(tenant.monthly_rent)}</p>
              <p><strong>Security Deposit:</strong> {formatCurrency(tenant.security_deposit)}</p>
              <p><strong>Start Date:</strong> {formatDate(tenant.start_date)}</p>
              <p><strong>End Date:</strong> {tenant.end_date ? formatDate(tenant.end_date) : 'N/A'}</p>
              <p><strong>Payment Due Date:</strong> {getOrdinalSuffix(tenant.payment_due_date)} of every month</p>
            </div>
          </div>
        ) : (
          <div className="detail-card">
            <h3>Previous Lease Information</h3>
            <div className="detail-content">
              <p><strong>Last Property:</strong> {tenant.property?.name || 'Unknown'}</p>
              <p>
                <strong>Last Active Period:</strong>
                {formatDate(tenant.start_date)} - {tenant.end_date ? formatDate(tenant.end_date) : 'N/A'}
              </p>
              {tenant.status === 'Inactive' && (
                <div className="inactive-notice">
                ℹ️ This tenant is no longer active. 
                  <Link to={`/settlements/${tenant.id}`} className="settlements-link">
                    Check the Settlements page
                  </Link>
                  for any outstanding balances.
                </div>
              )}
            </div>
          </div>
        )}
        {tenant.notes && (
          <div className="detail-card">
            <h3>Notes</h3>
            <div className="detail-content">
              <p>{tenant.notes}</p>
            </div>
          </div>
        )}

        <div className="detail-card documents-section">
          <h3>Documents</h3>
          <DocumentUpload tenantId={tenant.id} />
        </div>
      </div>

      <div className="tenant-data-section">
        <div className="tab-header">
          <button 
            className={`tab-btn ${activeTab === 'bills' ? 'active' : ''}`}
            onClick={() => setActiveTab('bills')}
          >
            Bills
          </button>
          <button 
            className={`tab-btn ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
          >
            Payments
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'bills' ? (
            <TenantBills tenantId={tenant.id} />
          ) : (
            <TenantPayments tenantId={tenant.id} />
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantDetails;
